import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Building2, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import EnvWarning from './EnvWarning';

const AuthPage: React.FC = () => {
  const { login, forgotPassword } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetInfo, setResetInfo] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isQuickLoginLoading, setIsQuickLoginLoading] = useState(false);
  const [quickBannerMessage, setQuickBannerMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
  
    try {
      await login(formData.email, formData.password);
      // Always redirect to default route; no role branching
      window.location.replace('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const getLockoutMessage = () => null;
  const getAttemptsWarning = () => null;

  const handleForgotPassword = async () => {
    setResetInfo(null);
    setResetError(null);
    if (!formData.email) {
      setResetError('Please enter your email first.');
      return;
    }
    try {
      setIsSendingReset(true);
      await forgotPassword(formData.email);
      setResetInfo('Check your email for the reset link.');
    } catch (e) {
      setResetError(e instanceof Error ? e.message : 'Failed to initiate password reset');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleQuickAdminLogin = async () => {
    try {
      setError('');
      setIsQuickLoginLoading(true);
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: 'hisyamudin@sarawaktourism.com',
        password: '11223344'
      });
      if (signInError) throw new Error(signInError.message);

      // Ensure session is established
      await supabase.auth.getUser();

      // Show confirmation banner briefly, then navigate
      setQuickBannerMessage('Logged in as hisyamudin (super_admin)');
      setTimeout(() => {
        window.location.assign('/debug');
      }, 200);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Quick login failed');
    } finally {
      setIsQuickLoginLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      {/* AppBootOK Indicator */}
      <div className="fixed top-2 left-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded text-xs font-mono">
        AppBootOK
      </div>

      {/* Quick Admin Login button (top-right) */}
      {/*
      <button
        type="button"
        onClick={handleQuickAdminLogin}
        disabled={isQuickLoginLoading}
        className="fixed top-2 right-2 text-xs px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed shadow"
      >
        {isQuickLoginLoading ? 'Logging in…' : 'Quick Admin Login'}
      </button>
      */}

      {/* Confirmation banner */}
      {quickBannerMessage && (
        <div className="fixed top-2 left-1/2 -translate-x-1/2 bg-green-100 dark:bg-green-900 text-green-900 dark:text-green-100 px-4 py-2 rounded-md shadow flex items-center space-x-2 z-50">
          <CheckCircle className="h-4 w-4" />
          <span className="text-sm font-medium">{quickBannerMessage}</span>
        </div>
      )}
      
      <div className="w-full max-w-md">
        {/* Environment Warning */}
        {!isSupabaseConfigured && <EnvWarning />}
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            PCR Project & Budget Tracker
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Welcome back</p>
        </div>

        {/* Auth Form */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
          {getLockoutMessage()}
          {getAttemptsWarning()}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {resetError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{resetError}</p>
              </div>
            )}

            {resetInfo && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">{resetInfo}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={isSendingReset}
                className="text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                {isSendingReset ? 'Sending…' : 'Forgot password?'}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © 2025 PCR Project & Budget Tracker. All rights reserved.
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Concept by Mohd Hisyamudin · Engineered with AI
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;